import { useCallback, useRef, useState } from 'react'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type {
  OrderLifecycleContractGovernanceEvent,
  SolanaOrderGovernanceChainState,
} from '@/types'
import { orderService } from '@/services/admin/orderService'

const ACTIVE_SOLANA_GOVERNANCE_STATUSES = new Set([
  'enqueueing',
  'queued',
  'ready',
  'executing',
  'retrying',
])

export type OrderPauseAvailability = {
  isLoading: boolean
  isUnavailable: boolean
  isPaused: boolean
  isClosed: boolean
  isProgramPaused: boolean
  hasPendingAction: boolean
}

const EMPTY_AVAILABILITY: OrderPauseAvailability = {
  isLoading: false,
  isUnavailable: false,
  isPaused: false,
  isClosed: false,
  isProgramPaused: false,
  hasPendingAction: false,
}

export const deriveSolanaOrderPauseAvailability = (
  chainState: SolanaOrderGovernanceChainState,
  events: OrderLifecycleContractGovernanceEvent[]
): Pick<
  OrderPauseAvailability,
  'isPaused' | 'isClosed' | 'isProgramPaused' | 'hasPendingAction'
> => ({
  isPaused:
    chainState.orderStateExists === true
    && chainState.orderClosed !== true
    && chainState.orderPaused === true,
  isClosed: chainState.orderClosed === true || chainState.orderStateExists !== true,
  isProgramPaused: chainState.programPaused === true,
  hasPendingAction: events.some(event => {
    const metadata = event.metadata
    const pauseSequence = metadata?.pauseSequence
    return metadata?.schemaVersion === '2'
      && metadata.scope === 'order'
      && typeof metadata.executionStatus === 'string'
      && typeof metadata.targetPaused === 'boolean'
      && typeof pauseSequence === 'number'
      && Number.isSafeInteger(pauseSequence)
      && pauseSequence >= 1
      && ACTIVE_SOLANA_GOVERNANCE_STATUSES.has(metadata.executionStatus)
  }),
})

export const useOrderPauseAvailability = () => {
  const requestVersion = useRef(0)
  const [state, setState] = useState<OrderPauseAvailability>(EMPTY_AVAILABILITY)

  const reset = useCallback(() => {
    requestVersion.current += 1
    setState(EMPTY_AVAILABILITY)
  }, [])

  const load = useCallback(async (orderId: string, mode: NetworkEnvironment) => {
    const version = requestVersion.current + 1
    requestVersion.current = version
    setState({ ...EMPTY_AVAILABILITY, isLoading: true })
    try {
      const [chainState, lifecycle] = await Promise.all([
        orderService.getSolanaOrderGovernanceState(orderId, mode),
        orderService.getOrderLifecycle(orderId, ['contractPauseResumeCloseEvents'], mode),
      ])
      if (requestVersion.current !== version) {
        return
      }
      setState({
        ...EMPTY_AVAILABILITY,
        ...deriveSolanaOrderPauseAvailability(
          chainState,
          lifecycle.contractPauseResumeCloseEvents ?? []
        ),
      })
    } catch {
      if (requestVersion.current === version) {
        setState({ ...EMPTY_AVAILABILITY, isUnavailable: true })
      }
    }
  }, [])

  return { ...state, load, reset }
}
